// ============================================
// COMPONENT - Modal de Timbraje de Folios SII
// ============================================

import { useState } from 'react';
import { MdClose, MdError } from 'react-icons/md';

interface TimbrarFoliosModalProps {
  isOpen: boolean;
  onClose: () => void;
  hayLoteVigente: boolean;
  isLoading: boolean;
  errorMessage?: string | null;
  onConfirm: (cantidadFolios: number, desactivarLotesVigentes: boolean) => void;
}

const CANTIDAD_MINIMA = 50;

export const TimbrarFoliosModal = ({
  isOpen,
  onClose,
  hayLoteVigente,
  isLoading,
  errorMessage,
  onConfirm,
}: TimbrarFoliosModalProps) => {
  const [cantidad, setCantidad] = useState<number>(CANTIDAD_MINIMA);
  const [desactivarLotesVigentes, setDesactivarLotesVigentes] = useState(false);

  if (!isOpen) return null;

  const cantidadInvalida = !cantidad || cantidad < CANTIDAD_MINIMA;

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const handleSubmit = () => {
    if (cantidadInvalida || isLoading) return;
    onConfirm(cantidad, desactivarLotesVigentes);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Carga automática de folios (SII)</h2>
          {!isLoading && (
            <button onClick={handleClose} className="p-1 rounded-lg hover:bg-gray-100">
              <MdClose className="text-xl text-gray-500" />
            </button>
          )}
        </div>

        <div className="px-6 py-6 space-y-4">
          <p className="text-sm text-gray-600">
            El sistema iniciará sesión en el SII con tu certificado digital y descargará un nuevo
            lote de folios de Boleta Electrónica (tipo 39), cargándolo automáticamente. La
            operación puede tardar hasta un minuto.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cantidad de folios (mínimo {CANTIDAD_MINIMA})
            </label>
            <input
              type="number"
              min={CANTIDAD_MINIMA}
              value={cantidad}
              onChange={(e) => setCantidad(Number(e.target.value))}
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
            {cantidadInvalida && (
              <p className="text-xs text-red-600 mt-1">
                La cantidad mínima a timbrar es {CANTIDAD_MINIMA} folios.
              </p>
            )}
          </div>

          {hayLoteVigente && (
            <label className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={desactivarLotesVigentes}
                onChange={(e) => setDesactivarLotesVigentes(e.target.checked)}
                disabled={isLoading}
                className="mt-1"
              />
              <span className="text-sm text-yellow-800">
                Los folios actuales aún tienen vigencia. ¿Deseas desactivarlos al cargar los
                nuevos? Si no marcas esta opción, se seguirán usando primero los folios
                vigentes y los nuevos quedarán disponibles recién cuando se agoten.
              </span>
            </label>
          )}

          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <MdError className="text-red-500 text-xl flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{errorMessage}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 rounded-b-2xl flex justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={cantidadInvalida || isLoading}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Cargando...</span>
              </>
            ) : (
              <span>Cargar folios</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
